import { useState } from "react";
import clsx from "clsx";
import PropTypes from "prop-types";

// MUI
import { Button, Dialog, Typography } from "@mui/material";
import { useGridApiRef } from "@mui/x-data-grid-premium";

// Helpers
import { getFormatUTCDate } from "@Common/helpers/Dates";
import { tableKeys } from "@/Constants/tableKeys";

// Components
import DataSmartTable from "@Layouts/components/Tables/DataSmartTable";
import CommonToolbar from "@Layouts/components/Tables/DataSmartTable/toolbar/CommonToolbar";
import NoRowsOverlay from "@Layouts/components/Tables/DataSmartTable/additional/NoRowsOverlay";
import ActivityDataDialog from "./ActivityDataDialog";
import CustomCell from "@/Layouts/components/Tables/DataSmartTable/additional/CustomCell";

//Constants
import {
	COLUMN_RESIZE_FACTOR,
	HEADER_CLASS_NAME,
	ONE_HUNDRED,
	ONE_HUNDRED_FIFTY,
	ONE_HUNDRED_SIXTY,
	ONE_HUNDRED_TWENTY,
	TAILWIND_FOR_DATA_SMART_TABLE,
} from "@/Constants/tables";
import { DATE_FORMAT } from "@/Constants/date";

export const isNavigationKey = (key) =>
	key === "Home" || key === "End" || key.indexOf("Arrow") === 0 || key.indexOf("Page") === 0 || key === " ";

export default function CustomerEngagementTrailTable(props) {
	const apiRef = useGridApiRef();
	const [open, setOpen] = useState(false);
	const [activityType, setActivityType] = useState(null);

	const sameProperties = {
		headerClassName: `table-header ${HEADER_CLASS_NAME} show-as-column fs-12`,
		resizable: false,
		sortable: false,
		width: ONE_HUNDRED,
		renderCell: (params) => <CustomCell value={params.formattedValue} hasTooltip maxWith={params.colDef.width} fontSize="12px" />,
	};

	const columns = [
		{
			field: "_id",
			headerName: "ID",
			hide: true,
			filterable: false,
		},
		{
			...sameProperties,
			field: "activity",
			headerName: "Activity",
			minWidth: ONE_HUNDRED,
			maxWidth: ONE_HUNDRED * COLUMN_RESIZE_FACTOR,
			resizable: true,
			cellClassName: (params) => {
				const isRequestField = params.id.includes("request_");
				return clsx({
					"no-focus table-noWrap": true,
					"cell-title cell-unset": isRequestField,
				});
			},
		},
		{
			...sameProperties,
			field: "action",
			headerName: "Action",
			width: ONE_HUNDRED_TWENTY,
			minWidth: ONE_HUNDRED_TWENTY,
			maxWidth: ONE_HUNDRED_TWENTY * COLUMN_RESIZE_FACTOR,
			resizable: true,
			cellClassName: (params) => {
				const isRequestField = params.id.includes("request_");
				return clsx({
					"no-focus table-noWrap": true,
					"cell-title cell-unset": isRequestField,
				});
			},
		},
		{
			...sameProperties,
			field: "cxmId",
			headerName: "Auto Alert Contact ID",
			cellClassName: (params) => {
				const isRequestField = params.id.includes("request_");
				return clsx({
					"no-focus table-noWrap": true,
					"cell-title cell-unset": isRequestField,
				});
			},
		},
		{
			...sameProperties,
			field: "customerName",
			headerName: "Customer Name",
			filterable: true,
			type: "string",
			width: ONE_HUNDRED_SIXTY,
			minWidth: ONE_HUNDRED_SIXTY,
			maxWidth: ONE_HUNDRED_SIXTY * COLUMN_RESIZE_FACTOR,
			resizable: true,
			cellClassName: (params) => {
				const isRequestField = params.id.includes("request_");
				return clsx({
					"no-focus table-noWrap": true,
					"cell-title cell-unset": isRequestField,
				});
			},
			valueGetter: (value) => `${value.firstName} ${value.lastName}`,
		},
		{
			...sameProperties,
			field: "partnerId",
			headerName: "Customer ID",
			width: ONE_HUNDRED_TWENTY,
			cellClassName: (params) => {
				const isRequestField = params.id.includes("request_");
				return clsx({
					"no-focus table-noWrap": true,
					"cell-title cell-unset": isRequestField,
				});
			},
		},
		{
			...sameProperties,
			field: "activityType",
			headerName: "Activity Type",
			cellClassName: (params) => {
				const isRequestField = params.id.includes("request_");
				return clsx({
					"no-focus table-noWrap": true,
					"cell-title cell-unset": isRequestField,
				});
			},
			renderCell: (params) => {
				return (
					<Button
						disableRipple
						variant="text"
						onClick={() => handleOpenDialog(params)}
						className="align-left-button !p-0 !mt-[3px] !underline"
					>
						<Typography className="regular-pt-sans-pro-narrow-link !text-[12px]" component="p">
							{params.value}
						</Typography>
					</Button>
				);
			},
		},
		{
			...sameProperties,
			field: "cxmActivityId",
			headerName: "Auto Alert Activity ID",
			cellClassName: (params) => {
				const isRequestField = params.id.includes("request_");
				return clsx({
					"no-focus table-noWrap": true,
					"cell-title cell-unset": isRequestField,
				});
			},
		},
		{
			...sameProperties,
			field: "initiatedByUser",
			headerName: "Activity Initiated by User",
			width: ONE_HUNDRED_FIFTY,
			minWidth: ONE_HUNDRED_FIFTY,
			maxWidth: ONE_HUNDRED_FIFTY * COLUMN_RESIZE_FACTOR,
			resizable: true,
			cellClassName: (params) => {
				const isRequestField = params.id.includes("request_");
				return clsx({
					"no-focus table-noWrap": true,
					"cell-title cell-unset": isRequestField,
				});
			},
			valueGetter: (value) => `${value.fullName} `,
		},
		{
			...sameProperties,
			field: "activityTimeStampUtc",
			headerName: "UTC Time",
			width: ONE_HUNDRED_TWENTY,
			type: "date",
			cellClassName: (params) => {
				const isRequestField = params.id.includes("request_");
				return clsx({
					"no-focus table-noWrap": true,
					"cell-title cell-unset": isRequestField,
				});
			},
			valueFormatter: (value) => getFormatUTCDate(value, DATE_FORMAT.usDateTime24hSeconds),
		},
	];

	const handleOpenDialog = (act) => {
		setActivityType(act);
		setOpen(true);
	};

	const handleCloseDialog = () => {
		setOpen(false);
	};

	return (
		<>
			<DataSmartTable
				filterStoreConfig={{
					key: tableKeys.DATA_PAGES.CUSTOMER_ENGAGEMENT_TRAIL,
				}}
				apiRef={apiRef}
				rows={props?.data ?? []}
				columns={columns}
				maxRows={16}
				enableCustomScrollbars
				hideFooter
				disableColumnMenu
				disableRowSelectionOnClick
				disableMultipleRowSelection
				checkboxSelection={false}
				editMode="row"
				columnVisibilityModel={{
					_id: false,
				}}
				slots={{
					toolbar: CommonToolbar,
					noRowsOverlay: NoRowsOverlay,
				}}
				slotProps={{
					toolbar: {
						filter: {
							hide: false,
						},
						search: {
							useQuickFilter: true,
						},
					},
					noRowsOverlay: {
						title: "No Customer Engagement Trail Data",
					},
				}}
				customProps={{
					tableClassName: {
						"functional-table": true,
						...TAILWIND_FOR_DATA_SMART_TABLE,
						"[&_.toolbar-actions]:!top-[0px]": true,
						"[&_.MuiDataGrid-main]:mt-[8px]": true,
					},
					hide: false,
				}}
			/>
			<Dialog
				open={open}
				disableEscapeKeyDown
				onClose={handleCloseDialog}
				className="activity-type-dialog [&_.MuiPaper-root]:!min-h-40"
				autoFocus={false}
				maxWidth="md"
				fullWidth
			>
				<ActivityDataDialog closeCallback={handleCloseDialog} data={activityType?.row} />
			</Dialog>
		</>
	);
}

CustomerEngagementTrailTable.propTypes = {
	data: PropTypes.array,
};
